import { useAbility } from '@casl/react';
import { useGetNotificationProvidersApi, useSaveNotificationProvidersApi } from 'apis/notification-providers';
import { AbilityContext } from 'configs/can';
import { Alert, AlertDescription, AlertIcon, Button, Skeleton, Stack, Text } from 'design-system';
import { useEffect } from 'react';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { EmailProviderCard } from '../components/email-provider-card';
import { PhoneOtpProvidersCard } from '../components/phone-otp-providers-card';

/**
 * Notification providers: who sends sign-in codes by phone (in order) and who delivers emails.
 * Every CMS user can see it; changing it needs `update` on NotificationProviderSettings (super admins).
 */
export const NotificationProvidersScreen = () => {
  const dispatch = useAppDispatch();
  const ability = useAbility(AbilityContext);
  const canUpdate = ability.can('update', 'NotificationProviderSettings');
  const query = useGetNotificationProvidersApi();
  const save = useSaveNotificationProvidersApi();
  const savingSection = save.isLoading ? save.variables?.section : undefined;

  useEffect(() => {
    dispatch(changeTitle('Notification Providers'));
  }, [dispatch]);

  if (query.isLoading) {
    return (
      <Stack spacing='md' maxW='800px'>
        <Skeleton h='280px' borderRadius='base' />
        <Skeleton h='200px' borderRadius='base' />
      </Stack>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Alert status='error' borderRadius='base' maxW='800px'>
        <AlertIcon />
        <AlertDescription flex={1}>The notification providers could not be loaded.</AlertDescription>
        <Button size='sm' variant='outline' colorScheme='red' onClick={() => query.refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Stack spacing='md' maxW='800px'>
      <Text fontSize='sm' color='gray.500'>
        API keys stay in the server settings. Only a provider the server has keys for can be chosen. A change reaches
        every server within a minute.
      </Text>
      <PhoneOtpProvidersCard
        settings={query.data.phoneOtp}
        canUpdate={canUpdate}
        isSaving={savingSection === 'phoneOtp'}
        onSave={(providers) => save.mutate({ section: 'phoneOtp', providers })}
      />
      <EmailProviderCard
        settings={query.data.email}
        canUpdate={canUpdate}
        isSaving={savingSection === 'email'}
        onSave={(provider) => save.mutate({ section: 'email', provider })}
      />
    </Stack>
  );
};
